import { PermissionGate } from "@components/PermissionGate";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import i18n from "src/i18n";
import { z } from "zod";

import { useUpdateClinicCollectionMethod } from "@hooks/mutations/clinic/useUpdateClinicCollectionMethod";

import { ClinicCollectionMethod } from "@customTypes/clinic";

import { StyledEditCollectionMethodModalContent } from "./EditCollectionMethodModal.styled";


const editFormSchema = () =>
  z.object({
    method: z.string().min(1, i18n.t("name-required")),
    price: z.number().min(0, i18n.t("price-required")),
  });

type Inputs = z.infer<ReturnType<typeof editFormSchema>>;

interface EditCollectionMethodModalProps {
  opened: boolean;
  close: () => void;
  collectionMethod: ClinicCollectionMethod | null;
}

export const EditCollectionMethodModal = ({
  opened,
  close,
  collectionMethod,
}: EditCollectionMethodModalProps) => {
  const { t } = useTranslation();

  const {
    mutate: updateCollectionMethod,
    isLoading: isUpdateCollectionMethodLoading,
  } = useUpdateClinicCollectionMethod();

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(editFormSchema()),
    initialValues: {
      method: "",
      price: 0,
    },
  });

  // Update form values when collectionMethod changes
  useEffect(() => {
    if (collectionMethod && opened) {
      form.setValues({
        method: collectionMethod.method || "",
        price: collectionMethod.price || 0,
      });
    }
  }, [collectionMethod, opened]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!opened) {
      form.reset();
    }
  }, [opened]);

  const handleFormSubmit = (values: Inputs) => {
    if (!collectionMethod?.id) return;

    updateCollectionMethod(
      {
        collectionMethodId: collectionMethod.id,
        collectionMethod: {
          method: values.method,
          price: values.price,
        },
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-collection-method")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md">
      <StyledEditCollectionMethodModalContent>
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <div className="form-field">
            <TextInput
              label={t("collection-method")}
              withAsterisk
              {...form.getInputProps("method")}
              key={form.key("method")}
              placeholder={t("eg", { example: "Blood Draw" })}
            />
          </div>

          <div className="form-field">
            <NumberInput
              label={t("price")}
              withAsterisk
              {...form.getInputProps("price")}
              key={form.key("price")}
              min={0}
              step={1}
              placeholder="25"
            />
          </div>

          <div className="form-actions">
            <PermissionGate permission="resources.edit"><Button
              ml={"auto"}
              loading={isUpdateCollectionMethodLoading}
              type="submit"
            >
              {t("update")}
            </Button></PermissionGate>
          </div>
        </form>
      </StyledEditCollectionMethodModalContent>
    </Modal>
  );
};
